import {Serializer} from '../interfaces/serializer';
import {
  ConditionalDimensionAction, ConditionalDimensionElseAction,
  ConditionalDimensionOperator,
  DataSetConditionalDimension,
  DataSetConditionalDimensionElse,
  DataSetConditionalDimensionRule,
  DataSetConditionalDimensionRuleCondition
} from '../interfaces/dimensions';
import {BehaviorSubject} from 'rxjs';
import {DimensionComponentObject, DimensionComponentObjects} from './dimension-component-object';

export type DatasetConditionalDimensionComponentObjects = Array<DatasetConditionalDimensionComponentObject>;

export type DatasetConditionalDimensionRuleConditionComponentObjects = Array<DatasetConditionalDimensionRuleConditionComponentObject>;

export class DatasetConditionalDimensionRuleConditionComponentObject {
  public payload: DataSetConditionalDimensionRuleCondition

  public readonly dimension$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  public readonly operator$: BehaviorSubject<ConditionalDimensionOperator> = new BehaviorSubject<ConditionalDimensionOperator>(null);
  public readonly value$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public readonly dimensionObject$: BehaviorSubject<DimensionComponentObject> = new BehaviorSubject<DimensionComponentObject>(null);
  public readonly valid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(rule: DataSetConditionalDimensionRuleCondition) {
    this.payload = rule;

    this.dimension$.next(rule.dimension);
    this.operator$.next(rule.operator);
    this.value$.next(rule.value);
  }

  public init(dimensions: DimensionComponentObjects, datasourceDimensions: DimensionComponentObjects, valid: BehaviorSubject<boolean>): void {
    const dimension: DimensionComponentObject = dimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === this.dimension$.value);
    const datasourceDimension: DimensionComponentObject = datasourceDimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === this.dimension$.value);

    this.dimensionObject$.next(dimension || datasourceDimension);
    this.valid$.next(!!dimension);

    if (!dimension) {
      valid.next(false);
    }
  }
}

export type DatasetConditionalDimensionRuleComponentObjects = Array<DatasetConditionalDimensionRuleComponentObject>;

export class DatasetConditionalDimensionRuleComponentObject {
  public payload: DataSetConditionalDimensionRule;

  public readonly rules$: BehaviorSubject<Array<DatasetConditionalDimensionRuleConditionComponentObjects>> = new BehaviorSubject<Array<DatasetConditionalDimensionRuleConditionComponentObjects>>([]);
  public readonly thenAction$: BehaviorSubject<ConditionalDimensionAction> = new BehaviorSubject<ConditionalDimensionAction>(null);
  public readonly then$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public readonly dimension$: BehaviorSubject<DimensionComponentObject> = new BehaviorSubject<DimensionComponentObject>(null);
  public readonly valid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(payload: DataSetConditionalDimensionRule) {
    this.payload = payload;

    this.rules$.next(payload.rules.map((rules: Array<DataSetConditionalDimensionRuleCondition>): DatasetConditionalDimensionRuleConditionComponentObjects => rules.map((rule: DataSetConditionalDimensionRuleCondition): DatasetConditionalDimensionRuleConditionComponentObject => new DatasetConditionalDimensionRuleConditionComponentObject(rule))));
    this.thenAction$.next(payload.thenAction);
    this.then$.next(payload.then);
  }

  public init(dimensions: DimensionComponentObjects, datasourceDimensions: DimensionComponentObjects, valid: BehaviorSubject<boolean>): void {
    if (this.thenAction$.value === 'dimension_value') {
      const dimension: DimensionComponentObject = dimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === this.then$.value);
      const datasourceDimension: DimensionComponentObject = datasourceDimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === this.then$.value);

      this.dimension$.next(dimension || datasourceDimension);
      this.valid$.next(!!dimension);

      if (!dimension) {
        valid.next(false);
      }
    }

    this.rules$.value.forEach((rules: DatasetConditionalDimensionRuleConditionComponentObjects) => rules.forEach((rule: DatasetConditionalDimensionRuleConditionComponentObject): void => rule.init(dimensions, datasourceDimensions, valid)));
  }
}

export type DatasetConditionalDimensionElseComponentObjects = Array<DatasetConditionalDimensionElseComponentObject>;

export class DatasetConditionalDimensionElseComponentObject {
  public payload: DataSetConditionalDimensionElse;

  public readonly action$: BehaviorSubject<ConditionalDimensionAction|ConditionalDimensionElseAction> = new BehaviorSubject<ConditionalDimensionAction|ConditionalDimensionElseAction>(null);
  public readonly value$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  public readonly dimension$: BehaviorSubject<DimensionComponentObject> = new BehaviorSubject<DimensionComponentObject>(null);
  public readonly valid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(payload: DataSetConditionalDimensionElse) {
    this.payload = payload;

    this.action$.next(payload.action);
    this.value$.next(payload.value);
    this.dimension$.next(null);
    this.valid$.next(false);
  }

  public init(dimensions: DimensionComponentObjects, datasourceDimensions: DimensionComponentObjects, valid: BehaviorSubject<boolean>): void {
    if (this.action$.value === 'dimension_value') {
      const dimension: DimensionComponentObject = dimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === this.value$.value);
      const datasourceDimension: DimensionComponentObject = datasourceDimensions.find((dimension: DimensionComponentObject): boolean => dimension.payload.attributes.slug === this.value$.value);

      this.dimension$.next(dimension || datasourceDimension);
      this.valid$.next(!!dimension);

      if (!dimension) {
        valid.next(false);
      }
    }
  }
}

export class DatasetConditionalDimensionComponentObject {
  public payload: Serializer<DataSetConditionalDimension>;

  public readonly name$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public readonly slug$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  public readonly rules$: BehaviorSubject<DatasetConditionalDimensionRuleComponentObjects> = new BehaviorSubject<DatasetConditionalDimensionRuleComponentObjects>([]);
  public readonly else$: BehaviorSubject<DatasetConditionalDimensionElseComponentObject> = new BehaviorSubject<DatasetConditionalDimensionElseComponentObject>(null);

  public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public valid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(payload: Serializer<DataSetConditionalDimension>) {
    this.update(payload);
  }

  public update(payload: Serializer<DataSetConditionalDimension>): void {
    this.payload = payload;

    this.name$.next(payload.attributes.name);
    this.slug$.next(payload.attributes.slug);
    this.valid$.next(true);

    this.rules$.next(payload.attributes.rules?.map((rule: DataSetConditionalDimensionRule): DatasetConditionalDimensionRuleComponentObject => new DatasetConditionalDimensionRuleComponentObject(rule)) || []);
    this.else$.next(new DatasetConditionalDimensionElseComponentObject(payload.attributes.else[0]));
  }

  public init(dimensions: DimensionComponentObjects, dataSourceDimensions: DimensionComponentObjects): void {
    this.rules$.value.forEach((rule: DatasetConditionalDimensionRuleComponentObject): void => rule.init(dimensions, dataSourceDimensions, this.valid$));
    this.else$.value.init(dimensions, dataSourceDimensions, this.valid$);
  }

}
